import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, AlertTriangle, X, ShoppingBag, Info } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const SESSION_TOAST_KEY = "sst.quotaToastShown";

/**
 * Stage C — Quota Bell + one-shot session toast.
 * - Polls /api/usage and lights up when status === critical / maxed_out / warning.
 * - 1× toast per browser session per status (so we don't nag).
 * - CTA always routes to /app/settings?tab=usage&focus=buy (proven, idempotent flow).
 * - Sponsored accounts: bell shows usage but CTA replaced with sponsor tooltip.
 * Owner / unlimited accounts: bell stays silent.
 */
export default function QuotaBell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const isSponsored = user?.account_type === "sponsored" || user?.license?.type === "sponsored";

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/usage`, { withCredentials: true });
        if (cancelled) return;
        setUsage(data);
        // One-shot toast per session, per threshold severity.
        const sessionKey = `${SESSION_TOAST_KEY}.${data.status}`;
        if (
          ["warning", "critical", "maxed_out"].includes(data.status) &&
          !sessionStorage.getItem(sessionKey)
        ) {
          sessionStorage.setItem(sessionKey, "1");
          showThresholdToast(data, navigate);
        }
      } catch {
        /* ignore — bell stays hidden */
      }
    };
    load();
    const id = setInterval(load, 60_000); // refresh every 60s
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Click outside to close
  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!usage) return null;
  if (usage.status === "unlimited") return null;

  const triggered = ["warning", "critical", "maxed_out"].includes(usage.status);
  const palette = paletteFor(usage.status);

  return (
    <div ref={containerRef} className="relative" data-testid="quota-bell">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        data-testid="quota-bell-button"
        className={`relative flex items-center justify-center w-10 h-10 rounded-full transition-all ${
          triggered ? "hover:bg-amber-50" : "hover:bg-slate-100"
        }`}
      >
        <Bell className={`w-5 h-5 ${triggered ? palette.iconColor : "text-slate-500"}`} />
        {triggered && (
          <span
            data-testid="quota-bell-badge"
            className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ${palette.dot} ring-2 ring-white`}
          >
            <span className={`absolute inset-0 rounded-full ${palette.dot} animate-ping opacity-75`} />
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[340px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50"
          data-testid="quota-bell-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <p className="text-sm font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
              Notifications
            </p>
            <button onClick={() => setOpen(false)} aria-label="Close" className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>

          {triggered ? (
            <div className="p-4">
              <div className={`flex items-start gap-3 ${palette.cardBg} border ${palette.cardBorder} rounded-lg p-3`}>
                <AlertTriangle className={`w-5 h-5 ${palette.iconColor} flex-shrink-0 mt-0.5`} />
                <div className="flex-1">
                  <p className="text-sm font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
                    {headlineFor(usage.status)}
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    {usage.pages_used_year.toLocaleString()} of {usage.pages_quota_year.toLocaleString()} pages used ·{" "}
                    {usage.remaining.toLocaleString()} remaining
                  </p>
                </div>
              </div>
              {isSponsored ? (
                <div className="mt-3 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700">
                    Need more pages? Contact your account sponsor to have additional pages added to your account.
                  </p>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setOpen(false);
                    navigate("/app/settings?tab=usage&focus=buy");
                  }}
                  data-testid="quota-bell-buy-cta"
                  className="w-full mt-3 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold py-2.5 rounded-lg transition-all"
                >
                  <ShoppingBag className="w-4 h-4" /> Buy More Pages
                </button>
              )}
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/app/settings?tab=usage");
                }}
                data-testid="quota-bell-view-usage"
                className="w-full mt-1.5 text-center text-xs font-medium text-slate-500 hover:text-slate-700 py-1.5"
              >
                View usage details →
              </button>
            </div>
          ) : (
            <div className="p-6 text-center">
              <p className="text-sm text-slate-500">You're all caught up.</p>
              <p className="text-xs text-slate-400 mt-1">
                {usage.pages_used_year.toLocaleString()} / {usage.pages_quota_year.toLocaleString()} pages used
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function paletteFor(status) {
  if (status === "maxed_out")
    return { iconColor: "text-red-600", dot: "bg-red-500", cardBg: "bg-red-50", cardBorder: "border-red-200" };
  if (status === "critical")
    return { iconColor: "text-orange-600", dot: "bg-orange-500", cardBg: "bg-orange-50", cardBorder: "border-orange-200" };
  return { iconColor: "text-amber-600", dot: "bg-amber-500", cardBg: "bg-amber-50", cardBorder: "border-amber-200" };
}

function headlineFor(status) {
  if (status === "maxed_out") return "You've reached your yearly limit";
  if (status === "critical") return "You're at 90%+ of your yearly pages";
  return "You're at 85% of your yearly pages";
}

function showThresholdToast(usage, navigate) {
  const palette = paletteFor(usage.status);
  const title = headlineFor(usage.status);
  const sub = `${usage.pages_used_year.toLocaleString()} / ${usage.pages_quota_year.toLocaleString()} pages used`;
  toast(title, {
    description: `${sub} · Top off before your next upload`,
    duration: 8000,
    className: `${palette.cardBg} ${palette.cardBorder}`,
    action: {
      label: "Buy More",
      onClick: () => navigate("/app/settings?tab=usage&focus=buy"),
    },
  });
}
